import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Pusher from 'pusher-js';
import { UserAgentApplication } from 'msal';

import { getGoogleSettings, getMicrosoftSettings, getPusherConfig } from 'services/requests';

import { IMicrosoftSettings, IPusherConfig } from 'services/types';
import { IParams } from 'types';

import { ReactComponent as MicrosoftIcon } from 'assets/microsoft.svg';
import { ReactComponent as GoogleIcon } from 'assets/google.svg';

import Loader from 'components/Loader/Loader';

interface IProps {
  onSuccess: () => void;
}

const LoginButtons: React.FC<IProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [pusherConfig, setPusherConfig] = useState<IPusherConfig | undefined>(undefined);
  const [params, setParams] = useState<IParams | undefined>(undefined);
  const [msalInitDone, setMsalInitDone] = useState<boolean>(false);
  const [redirectMicrosoftToken, setRedirectMicrosoftToken] = useState<string | null>(null);
  const redirectMicrosoftTokenRef = useRef<string | null>(null);
  const [microsoftSettings, setMicrosoftSettings] = useState<IMicrosoftSettings | null>(null);
  const [microsoftSettingsLoading, setMicrosoftSettingsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const pusher = useMemo(() => {
    if (pusherConfig?.cluster && pusherConfig?.key) {
      return new Pusher(pusherConfig.key, {
        cluster: pusherConfig.cluster,
        channelAuthorization: {
          transport: 'ajax',
          endpoint: `${import.meta.env.VITE_BASE_API_URL}/common/authorize-auth-pusher`,
        },
      });
    }
  }, [pusherConfig]);

  const handleSendMessage = (data: { token: string; type: string }): void => {
    try {
      const sessionId = localStorage.getItem('sessionId') || params?.sessionId;
      if (pusher?.key && sessionId) {
        setLoading(true)
        const ch = pusher.subscribe(`private-${sessionId}`);

        ch.bind('pusher:subscription_succeeded', () => {
          ch.trigger('client-message', data);
          onSuccess();
          setLoading(false)
        });
      }
    } catch (e) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = (): void => {
    try {
      setErrorMessage(null);
      if (microsoftSettingsLoading || !microsoftSettings) {
        setErrorMessage('Microsoft auth is not ready yet. Please try again in a moment.');
        setLoading(false);
        return;
      }

      setLoading(true);
      const redirectUri = window.location.href.split('#')[0].split('?')[0];
      const msalObject = new UserAgentApplication({
        auth: {
          clientId: microsoftSettings.auth.clientId,
          authority: microsoftSettings.auth.authority,
          redirectUri,
        },
      });

      const sessionId = params?.sessionId ?? searchParams.get('sessionId');
      if (sessionId) {
        localStorage.setItem('sessionId', sessionId);
      }

      console.log('Starting Microsoft loginRedirect...');
      try {
        msalObject.loginRedirect({ scopes: ['User.Read'] });
        // If redirect is blocked/suppressed, user will stay on loader.
        // Provide a small fallback tologinPopup surface the issue.
        window.setTimeout(() => {
          setLoading(false);
          setErrorMessage('Microsoft login did not start. Please check console and popup/redirect permissions.');
        }, 8000);
      } catch (e) {
        console.error('loginRedirect failed:', e);
        setErrorMessage('Microsoft loginRedirect failed. Check console for details.');
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  type GoogleOAuth2Window = Window & {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string }) => void;
          }) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  };

  const win = window as unknown as GoogleOAuth2Window;

  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (win.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        if (win.google?.accounts?.oauth2) {
          resolve();
        } else {
          reject(new Error('Google script loaded but OAuth2 not available.'));
        }
      };

      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services script.'));
      };

      document.head.appendChild(script);
    });
  };

  const handleGoogleLogin = async (): Promise<void> => {
    setLoading(true);
    try {
      const { clientId } = await getGoogleSettings();
      await loadGoogleScript();

      const client = win.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'email profile openid',
        callback: (response: { access_token: string }) => {
          if (response?.access_token) {
            handleSendMessage({
              type: 'google',
              token: response.access_token,
            });
          } else {
            console.error('No access token received');
            setLoading(false);
          }
        },
      });

      client.requestAccessToken();
    } catch (err) {
      console.error('Google login failed:', err);
      setLoading(false);
    }
  };

  const onMount = useCallback(() => {
    setLoading(true);
    getPusherConfig()
      .then(res => {
        setPusherConfig(res);

        const type = searchParams.get('type');
        const sessionId = searchParams.get('sessionId');
        if (res && type && sessionId) {
          setParams({ type, sessionId });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Prefetch MSAL settings so button click doesn't rely on async fetch.
  useEffect(() => {
    setMicrosoftSettingsLoading(true);
    getMicrosoftSettings()
      .then(res => {
        setMicrosoftSettings(res);
      })
      .catch(err => {
        console.error('getMicrosoftSettings failed:', err);
        setErrorMessage('Failed to load Microsoft auth settings.');
      })
      .finally(() => {
        setMicrosoftSettingsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!microsoftSettings) return;

    setMsalInitDone(false);
    const redirectUri = window.location.href.split('#')[0].split('?')[0];
    const msalObject = new UserAgentApplication({
      auth: {
        clientId: microsoftSettings.auth.clientId,
        authority: microsoftSettings.auth.authority,
        redirectUri,
      },
    });

    // MSAL redirect flow stores the response internally; read it here after redirect back.
    msalObject.handleRedirectCallback((authErr, response) => {
      if (authErr) {
        console.error('MSAL redirect error:', authErr);
        return;
      }

      if (response?.idToken?.rawIdToken) {
        redirectMicrosoftTokenRef.current = response.idToken.rawIdToken;
        setRedirectMicrosoftToken(response.idToken.rawIdToken);
      }
    });

    setMsalInitDone(true);
  }, [microsoftSettings]);

  useEffect(() => {
    if (params && pusher && msalInitDone) {
      if (params.type === 'Microsoft') {
        // If we already received the redirect response, don't start another loginRedirect.
        if (!redirectMicrosoftTokenRef.current) {
          handleMicrosoftLogin();
        }
      }
      if (params.type === 'Google') {
        handleGoogleLogin();
      }
    }
  }, [params, pusher, msalInitDone]);

  useEffect(() => {
    if (params?.sessionId) {
      localStorage.setItem('sessionId', params?.sessionId)
    }
  }, [params]);

  useEffect(() => {
    if (redirectMicrosoftToken && params?.type === 'Microsoft' && pusher) {
      handleSendMessage({ type: 'microsoft', token: redirectMicrosoftToken });
      // Prevent sending the same token multiple times.
      redirectMicrosoftTokenRef.current = null;
      setRedirectMicrosoftToken(null);
    }
  }, [redirectMicrosoftToken, params?.type, pusher]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  if (loading) return <Loader />;
  return (
    <div className="list">
      <button className="button" onClick={handleMicrosoftLogin}>
        Microsoft
        <MicrosoftIcon />
      </button>

      <button className="button" onClick={() => {
        handleGoogleLogin()
      }}>
        Google
        <GoogleIcon />
      </button>
      {errorMessage && <div className="error-message">{errorMessage}</div>}
    </div>
  );
};

export default LoginButtons;
